import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../lib/api';

const MatchResult = ({ match }: { match: any }) => {
  const navigate = useNavigate();
  const [replayOpen, setReplayOpen] = useState(false);
  const [replayLoading, setReplayLoading] = useState(false);
  const [replayError, setReplayError] = useState('');
  const matchCost = match.matchCost ?? null;

  const score = match.score ? JSON.parse(match.score) : {};

  const team1 = match.participants[0];
  const team2 = match.participants[1];
  const team1Key = team1?.teamId || team1?.userId || team1?.side || 'left';
  const team2Key = team2?.teamId || team2?.userId || team2?.side || 'right';
  const anonymousLabels = {
    left: 'Modrý tým',
    right: 'Červený tým'
  };
  const team1Name = team1?.team?.name || team1?.user?.name || (team1?.side === 'right' ? anonymousLabels.right : anonymousLabels.left);
  const team2Name = team2?.team?.name || team2?.user?.name || (team2?.side === 'left' ? anonymousLabels.left : anonymousLabels.right);

  const winner = match.participants.find((p: any) => p.winner === true);
  const winnerId = winner?.teamId || winner?.userId || winner?.side;
  const winnerName = winner?.team?.name || winner?.user?.name || 'Neznámý';

  const isTeam1Winner = winner ? winnerId === team1Key : false;

  const replayPayload = useMemo(() => {
    const participants = match.participants.map((p: any) => ({
      userId: p.userId,
      teamId: p.teamId,
      side: p.side,
    }));

    return {
      sessionId: match.playSessionId,
      mode: match.mode,
      participants,
    };
  }, [match]);

  const handleReplay = async () => {
    setReplayError('');
    setReplayLoading(true);
    try {
      const response = await api.post('/matches', replayPayload);
      navigate(`/matches/${response.data.id}`);
    } catch (err: any) {
      console.error('Failed to start replay', err);
      setReplayError(err.response?.data?.message || 'Nelze spustit nový zápas. Zkontrolujte zůstatek nebo session.');
    } finally {
      setReplayLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8 sm:mb-12 animate-fade-in">
          <div className="text-7xl sm:text-9xl mb-4 animate-bounce-slow">🏆</div>
          <h2 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent mb-2">
            Zápas je u konce!
          </h2>
          <p className="text-gray-600 text-lg">Souboj skončil</p>
        </div>

        {winner && (
          <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 p-8 sm:p-12 rounded-3xl shadow-glow-lg mb-8 text-center animate-slide-up">
            <div className="text-5xl sm:text-6xl mb-4">🎉</div>
            <div className="text-white">
              <p className="text-2xl sm:text-3xl font-bold mb-2">Vítěz</p>
              <p className="text-4xl sm:text-6xl font-bold">{winnerName}</p>
            </div>
          </div>
        )}

        <div className="bg-white/80 backdrop-blur-lg p-6 sm:p-10 rounded-3xl shadow-card-hover border border-white mb-8 animate-slide-up" style={{ animationDelay: '0.1s' }}>
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-8">Konečné skóre</h3>
          <div className="grid grid-cols-2 gap-6 sm:gap-8">
            <div className={`p-6 sm:p-8 rounded-3xl text-center transform transition-all duration-300 ${
              isTeam1Winner
                ? 'bg-gradient-to-br from-primary-500 to-secondary-500 text-white scale-105'
                : 'bg-gray-100 text-gray-900'
            }`}>
              <div className="text-4xl sm:text-5xl mb-4">
                {isTeam1Winner ? '🥇' : '🥈'}
              </div>
              <h4 className="text-lg sm:text-xl font-bold mb-4 truncate px-2">{team1Name}</h4>
              <div className="text-5xl sm:text-7xl font-bold">{score[team1Key] || 0}</div>
              <div className="text-sm sm:text-base mt-2 opacity-80">góly</div>
            </div>

            <div className={`p-6 sm:p-8 rounded-3xl text-center transform transition-all duration-300 ${
              !isTeam1Winner
                ? 'bg-gradient-to-br from-primary-500 to-secondary-500 text-white scale-105'
                : 'bg-gray-100 text-gray-900'
            }`}>
              <div className="text-4xl sm:text-5xl mb-4">
                {!isTeam1Winner ? '🥇' : '🥈'}
              </div>
              <h4 className="text-lg sm:text-xl font-bold mb-4 truncate px-2">{team2Name}</h4>
              <div className="text-5xl sm:text-7xl font-bold">{score[team2Key] || 0}</div>
              <div className="text-sm sm:text-base mt-2 opacity-80">góly</div>
            </div>
          </div>
        </div>

        <div className="bg-white/60 backdrop-blur-sm p-6 sm:p-8 rounded-3xl border border-white/50 mb-8 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <h4 className="font-bold text-gray-900 mb-4 text-lg text-center">📊 Statistiky zápasu</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl sm:text-3xl font-bold text-primary-600">{(score[team1Key] || 0) + (score[team2Key] || 0)}</div>
              <div className="text-xs sm:text-sm text-gray-600 mt-1">Celkem gólů</div>
            </div>
            <div>
              <div className="text-2xl sm:text-3xl font-bold text-secondary-600">{match.mode === 'individual' ? '1v1' : 'Tým'}</div>
              <div className="text-xs sm:text-sm text-gray-600 mt-1">Režim zápasu</div>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <div className="text-2xl sm:text-3xl font-bold text-accent-600">Dokončeno</div>
              <div className="text-xs sm:text-sm text-gray-600 mt-1">Stav</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
          <Link
            to="/leaderboard"
            className="py-4 px-6 font-bold text-white text-center bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl shadow-lg hover:shadow-glow hover:scale-105 transform transition-all duration-300"
          >
            Zobrazit žebříček 🏆
          </Link>
          <button
            onClick={() => setReplayOpen(true)}
            className="py-4 px-6 font-bold text-primary-600 text-center bg-white border-2 border-primary-500 rounded-2xl shadow-lg hover:bg-primary-50 hover:scale-105 transform transition-all duration-300"
          >
            Hrát znovu 🎮
          </button>
        </div>

        {replayOpen && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-3xl shadow-card-hover max-w-md w-full p-6 space-y-4">
              <h3 className="text-2xl font-bold text-gray-900 text-center">Zahájit další hru?</h3>
              <p className="text-gray-700 text-center">
                Opravdu si přejete zahájit další hru {matchCost ? `za ${matchCost} tokenů` : ''}?
              </p>
              {replayError && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-2 rounded-xl">
                  {replayError}
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleReplay}
                  disabled={replayLoading}
                  className="flex-1 py-3 font-bold text-white bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl hover:shadow-glow transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {replayLoading ? 'Spouštím...' : 'Ano, začít'}
                </button>
                <button
                  onClick={() => {
                    setReplayOpen(false);
                    setReplayError('');
                  }}
                  className="flex-1 py-3 font-bold text-gray-700 bg-gray-100 rounded-2xl border border-gray-200 hover:bg-gray-200 transition-all duration-200"
                >
                  Zrušit
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MatchResult;
